import type { Client } from '../../../api/client.js';

/**
 * Comptes rendus du dossier (PARITE-4, réunion du 16/10 #31) : lectures et écritures par les seules routes du contrat
 * (`comptesRendus.*`). Les droits sont ceux de la session (permissions calculées par le serveur), jamais recalculés ici.
 */
export type NatureCompteRendu = 'entretien' | 'appel';
export type StatutCompteRendu = 'a_faire' | 'a_relire' | 'valide';

export interface CompteRendu {
  id: string;
  dossierId: string;
  personId: string | null;
  kind: NatureCompteRendu;
  title: string;
  status: StatutCompteRendu;
  sourceText: string | null;
  body: string | null;
  createdAt: string;
  createdByDisplayName: string;
  generatedAt: string | null;
  validatedAt: string | null;
  validatedByDisplayName: string | null;
  rowVersion: number;
}

export interface NouveauCompteRendu {
  kind: NatureCompteRendu;
  title: string;
  sourceText?: string;
}

export interface SourceComptesRendus {
  lister(): Promise<CompteRendu[]>;
  creer(e: NouveauCompteRendu): Promise<CompteRendu>;
  /** Sans texte : celui déjà enregistré sur le compte rendu. */
  generer(id: string, sourceText?: string): Promise<CompteRendu>;
  corriger(id: string, body: string, versionLue: number): Promise<CompteRendu>;
  valider(id: string, versionLue: number): Promise<CompteRendu>;
}

export const PERMISSION_ECRIRE = 'compte_rendu:write:dossier';
export const PERMISSION_VALIDER = 'compte_rendu:validate:dossier';

export function creerSourceComptesRendus(client: Client, dossierId: string): SourceComptesRendus {
  const parCompteRendu = (compteRenduId: string) => ({ compteRenduId });
  return {
    lister: () => client.appeler<CompteRendu[]>('comptesRendus.lister', { parametres: { dossierId } }),
    creer: (e) =>
      client.appeler<CompteRendu>('comptesRendus.creer', {
        parametres: { dossierId },
        corps: { kind: e.kind, title: e.title.trim(), ...(e.sourceText?.trim() ? { sourceText: e.sourceText.trim() } : {}) },
      }),
    generer: (id, sourceText) =>
      client.appeler<CompteRendu>('comptesRendus.generer', {
        parametres: parCompteRendu(id),
        corps: sourceText?.trim() ? { sourceText: sourceText.trim() } : {},
      }),
    corriger: (id, body, versionLue) =>
      client.appeler<CompteRendu>('comptesRendus.modifier', { parametres: parCompteRendu(id), corps: { body }, versionLigne: versionLue }),
    valider: (id, versionLue) =>
      client.appeler<CompteRendu>('comptesRendus.valider', { parametres: parCompteRendu(id), corps: { rowVersion: versionLue } }),
  };
}
